import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Icon } from "../Icon";
import { useAuth } from "../auth";
import { PageHero, useSeo } from "../ui";

const EMPTY = {
  email: "", password: "", password2: "", name: "", phone: "", company: "",
  website: "", // 봇 트랩
};

export default function Signup() {
  useSeo({
    title: "회원가입 | I.META 아이닷메타",
    description: "I.META 회원으로 가입하시면 제품 구매와 회원 전용 자료 다운로드를 이용하실 수 있습니다.",
    path: "/signup",
  });

  const { signup, member } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [agree, setAgree] = useState({ terms: false, privacy: false, age: false, marketing: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const tog = (k) => (e) => setAgree((a) => ({ ...a, [k]: e.target.checked }));

  const allRequired = agree.terms && agree.privacy && agree.age;
  const toggleAll = (e) => {
    const v = e.target.checked;
    setAgree({ terms: v, privacy: v, age: v, marketing: v });
  };

  async function onSubmit(e) {
    e.preventDefault();
    if (busy) return;

    if (form.password !== form.password2) {
      setError("비밀번호가 서로 다릅니다.");
      return;
    }
    if (form.password.length < 8) {
      setError("비밀번호는 8자 이상으로 정해주세요.");
      return;
    }
    if (!allRequired) {
      setError("필수 항목에 모두 동의하셔야 가입할 수 있습니다.");
      return;
    }

    setBusy(true);
    setError("");
    try {
      const data = await signup({
        email: form.email.trim(),
        password: form.password,
        name: form.name.trim(),
        phone: form.phone.trim(),
        company: form.company.trim(),
        terms_agree: agree.terms,
        privacy_agree: agree.privacy,
        age_agree: agree.age,
        marketing_agree: agree.marketing,
        website: form.website,
      });
      setDone(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  if (member && !done) {
    return (
      <PageHero kicker="SIGN UP" title="이미 로그인되어 있습니다" desc={`${member.name} 님으로 로그인 중입니다.`}>
        <div className="hero__actions">
          <Link to="/account" className="btn btn--primary">내 정보</Link>
          <Link to="/" className="btn btn--ghost">홈으로</Link>
        </div>
      </PageHero>
    );
  }

  if (done) {
    return (
      <PageHero
        kicker="SIGN UP"
        title="가입이 완료되었습니다"
        desc={
          done.verify_mail_sent
            ? "보내드린 인증 메일의 링크를 눌러 이메일 인증을 마쳐주세요. 메일이 보이지 않으면 스팸함도 확인해주세요."
            : "가입이 완료되었습니다. 이메일 인증 메일은 잠시 후 다시 요청하실 수 있습니다."
        }
      >
        <div className="hero__actions">
          <button type="button" className="btn btn--primary" onClick={() => navigate("/")}>
            홈으로 <Icon.ArrowRight width="16" height="16" aria-hidden="true" focusable="false" />
          </button>
          <Link to="/account" className="btn btn--ghost">내 정보</Link>
        </div>
      </PageHero>
    );
  }

  return (
    <>
      <PageHero
        kicker="SIGN UP"
        title="회원가입"
        desc="제품 구매와 회원 전용 자료 다운로드에 필요합니다. 가입은 무료입니다."
      />

      <section className="section section--tight">
        <form className="auth-card form" onSubmit={onSubmit} noValidate>
          <div className="form__field">
            <label className="form__label" htmlFor="su-email">
              이메일 <span className="form__req" aria-hidden="true">*</span>
            </label>
            <input
              id="su-email" className="form__input" type="email" required maxLength={200}
              autoComplete="email" value={form.email} onChange={upd("email")} disabled={busy}
            />
            <p className="form__hint">로그인 아이디로 쓰입니다.</p>
          </div>

          <div className="form__row">
            <div className="form__field">
              <label className="form__label" htmlFor="su-pw">
                비밀번호 <span className="form__req" aria-hidden="true">*</span>
              </label>
              <input
                id="su-pw" className="form__input" type="password" required minLength={8} maxLength={200}
                autoComplete="new-password" value={form.password} onChange={upd("password")} disabled={busy}
              />
            </div>
            <div className="form__field">
              <label className="form__label" htmlFor="su-pw2">
                비밀번호 확인 <span className="form__req" aria-hidden="true">*</span>
              </label>
              <input
                id="su-pw2" className="form__input" type="password" required minLength={8} maxLength={200}
                autoComplete="new-password" value={form.password2} onChange={upd("password2")} disabled={busy}
              />
            </div>
          </div>
          <p className="form__hint" style={{ marginTop: -8, marginBottom: 14 }}>8자 이상으로 정해주세요.</p>

          <div className="form__row">
            <div className="form__field">
              <label className="form__label" htmlFor="su-name">
                이름 <span className="form__req" aria-hidden="true">*</span>
              </label>
              <input
                id="su-name" className="form__input" type="text" required maxLength={60}
                autoComplete="name" value={form.name} onChange={upd("name")} disabled={busy}
              />
            </div>
            <div className="form__field">
              <label className="form__label" htmlFor="su-phone">
                연락처 <span className="form__opt">(선택)</span>
              </label>
              <input
                id="su-phone" className="form__input" type="tel" maxLength={40}
                autoComplete="tel" value={form.phone} onChange={upd("phone")} disabled={busy}
              />
            </div>
          </div>

          <div className="form__field">
            <label className="form__label" htmlFor="su-company">
              회사 · 기관명 <span className="form__opt">(선택)</span>
            </label>
            <input
              id="su-company" className="form__input" type="text" maxLength={200}
              autoComplete="organization" value={form.company} onChange={upd("company")} disabled={busy}
            />
          </div>

          {/* 봇 트랩 */}
          <div className="form__trap" aria-hidden="true">
            <label htmlFor="su-website">홈페이지</label>
            <input id="su-website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={upd("website")} />
          </div>

          <div className="auth-terms">
            <div className="form__agree auth-terms__all">
              <input id="su-all" type="checkbox" checked={allRequired && agree.marketing} onChange={toggleAll} disabled={busy} />
              <label htmlFor="su-all"><strong>전체 동의</strong></label>
            </div>

            <div className="form__agree">
              <input id="su-terms" type="checkbox" checked={agree.terms} onChange={tog("terms")} disabled={busy} />
              <label htmlFor="su-terms">
                <Link to="/terms" target="_blank">이용약관</Link>에 동의합니다.
                <span className="form__req" aria-hidden="true"> *</span>
              </label>
            </div>

            <div className="form__agree">
              <input id="su-privacy" type="checkbox" checked={agree.privacy} onChange={tog("privacy")} disabled={busy} />
              <label htmlFor="su-privacy">
                <Link to="/privacy" target="_blank">개인정보 수집·이용</Link>에 동의합니다.
                <span className="form__req" aria-hidden="true"> *</span>
              </label>
            </div>

            <div className="form__agree">
              <input id="su-age" type="checkbox" checked={agree.age} onChange={tog("age")} disabled={busy} />
              <label htmlFor="su-age">
                만 14세 이상입니다.
                <span className="form__req" aria-hidden="true"> *</span>
              </label>
            </div>

            <div className="form__agree">
              <input id="su-mkt" type="checkbox" checked={agree.marketing} onChange={tog("marketing")} disabled={busy} />
              <label htmlFor="su-mkt">
                신제품·자료 소식 메일 수신에 동의합니다. <span className="form__opt">(선택)</span>
              </label>
            </div>
          </div>

          {error && <p className="form__error" role="alert">{error}</p>}

          <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
            {busy ? "가입 중…" : "가입하기"}
            {!busy && <Icon.ArrowRight width="16" height="16" aria-hidden="true" focusable="false" />}
          </button>

          <p className="form__note">
            이미 계정이 있으신가요? <Link to="/login">로그인으로 돌아가기</Link>
          </p>
        </form>
      </section>
    </>
  );
}
